import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../../components/TextInputs";
import useDebounce from "../../../../hooks/useDebounce";
import {
  setBrandCurrentPage,
  setBrandPageLimit,
  setBrandTotalCount,
  setBrandTotalPages,
} from "../../../../lib/slices/pagination/brandPageSlice";
import { setBrand } from "../../../../lib/slices/searchKeysSlice";
import { getBrands, updateBrand } from "../../../../services/brandApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { AddEditBrandDialog } from "./AddEditBrandDialog";

// Header Data For Promotional Block Table
const headerData = ["createdAt", "name", "description", "status","featured", "actions"];

// Table Rows For Promotional Block Table
const tableRows = [
  "created_date",
  "brand_name",
  "brand_description",
  "status",
  "featured",
  "actions",
];

// Promotional Block Component
export function Brands() {
  // Dispatch Hook
  const dispatch = useDispatch();
  // Open Dialog Ref
  const openDialog = useRef(null);
  // Loading State
  const [isLoading, setIsLoading] = useState(true);
  // All Promotional Blocks
  const [allBrands, setAllBrands] = useState([]);
  // Translation Hook
  const { t } = useTranslation(["catalogue", "common"]);
  // Search Keys
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  // Get Pagination for Promotional Blocks
  const pagination = useSelector((state) => state.brandPaginationReducer);
  // Debounced Search Term
  const debouncedSearchTerm = useDebounce(searchKeys.brand, 1000, true, () => {
    dispatch(setBrandCurrentPage(1));
  });

  // Fetch All Brands
  const handleFetchAllBrands = async (search, page, limit, loading = true) => {
    try {
      const params = {
        search,
        page,
        limit,
      };
      setIsLoading(loading);
      const response = await getBrands(params);
      if (response.data.status === "success") {
        let data = response?.data.data?.data;
        const pagination = response?.data.data.pagination;
        setAllBrands(data);
        dispatch(setBrandPageLimit(pagination.limit));
        dispatch(setBrandCurrentPage(pagination.page));
        dispatch(setBrandTotalCount(pagination.total));
        dispatch(setBrandTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch All Promotional Blocks On Component Mount
  useEffect(() => {
    handleFetchAllBrands(
      debouncedSearchTerm,
      pagination.page,
      pagination.limit,
    );
  }, [debouncedSearchTerm, pagination.page, pagination.limit]);

  // Handle Edit Promotional Block
  const handleEdit = (params) => {
    console.log(params, "sdskadaskjdasjkdsjakdjkasdjksajkdjksadjkasjk");
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: handleFetchAllBrands,
    });
  };

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setBrandCurrentPage(page));
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    console.log(event, "event");
    const limit = parseInt(event.target.value, 10);
    dispatch(setBrandPageLimit(limit));
    dispatch(setBrandCurrentPage(1));
  };

  // Handle Change Status Promotional Block
  const handleChangeStatus = async (event, row, key) => {
    try {
      let payload = {
        [key]: event,
      };
      const response = await updateBrand(row._id, payload);
      if (response.data.status === "success") {
        handleFetchAllBrands(
          searchKeys.brand,
          pagination.page,
          pagination.limit,
          false,
        );
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  // Prepare Export Data For Promotional Block
  const prepareExportData = () => {
    return allBrands?.map((item) => ({
      "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
      "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
      "Name In Romanian": item.title?.ro || "-",
      "Name In English": item.title?.en || "-",
      Status: item.status ? "Active" : "Inactive",
    }));
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          handleClickBtn={() => {
            openDialog.current.openDialog({
              type: "add",
              data: null,
              getData: handleFetchAllBrands,
            });
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        />
        <CSVLink
          data={prepareExportData()}
          filename={"brand.csv"}
          className="csv-link"
          target="_blank"
        >
          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            styleProps={{
              width: "auto",
              height: 40,
            }}
          />
        </CSVLink>
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.brand && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(setBrand(""));
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.brand}
          onChange={(e) => {
            dispatch(setBrand(e.target.value));
          }}
        />
      </Stack>
    );
  };

  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        minWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allBrands || []}
        showActions={true}
        showEdit={true}
        handleEdit={handleEdit}
        handleChangeStatus={(e, row, key) =>
          handleChangeStatus(e, row, "status")
        }
        handleChangeFeatured={(e, row, key) =>
          handleChangeStatus(e, row, "isFeatured")
        }
        paginatedData={pagination}
        showDynamicPagination={true}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 30, 50, 100]}
        isLoading={isLoading}
      />
    );
  }, [allBrands, isLoading]);

  return (
    <React.Fragment>
      <PaperContainer
        title={t("brands")}
        subtitle={t("viewBrands")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        {renderTable}
      </PaperContainer>

      <AddEditBrandDialog ref={openDialog} />
    </React.Fragment>
  );
}
